import chroma from 'chroma-js';

import { isValidColor } from '@bv/utils';

/**
 * Color scale levels
 */
export type ColorScaleLevel = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

/**
 * Color scale object with hex values
 */
type ColorScale = Record<ColorScaleLevel, string>;

/**
 * Generates a complete color scale from 50 to 900 based on a base color (level 500)
 * Uses reference colors to maintain consistent lightness, chroma, and hue relationships
 *
 * @param baseColor - Base color at level 500 (hex, rgb, hsl, etc.)
 * @returns Object containing all scale levels with hex values
 * @returns If the provided color is invalid, Object containing all scale levels with as black
 * @example
 * generateColorScale('#095ea6')
 * // Returns: { 50: '#eff7fe', 100: '#cbeefa', ..., 900: '#011b4f' }
 * @example
 * generateColorScale('invalid')
 * // Returns: { 50: '#000000', 100: '#000000', ..., 900: '#000000' }
 */
export const generateColorScale = (baseColor: string): ColorScale => {
  // Scale levels to generate
  const keys: ColorScaleLevel[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

  // Guard clause: If the input is not a valid color, return a full
  // black scale as a fallback instead of throwing an error.
  if (!isValidColor(baseColor)) {
    const scale = {} as ColorScale;
    keys.forEach((key) => (scale[key] = '#000000'));
    return scale;
  }

  // Lightness curve - defines how light/dark each level should be
  const lightnessCurve = [97.5, 93.2, 86.0, 73.8, 61.5, 40.7, 31.5, 23.8, 17.0, 12.5];

  // Chroma (saturation) curve - as ratios of base color chroma
  const chromaRatios = [0.15, 0.35, 0.6, 0.85, 0.95, 1.0, 0.95, 0.85, 0.75, 0.65];

  // Convert base color to LCH color space for better perceptual uniformity
  const base = chroma(baseColor);
  const [_, baseC, baseH] = base.lch();

  const scale = {} as ColorScale;
  const baseIndex = 5; // Index of base color (level 500)

  keys.forEach((key, index) => {
    // Keep the base color unchanged at level 500
    if (key === 500) {
      scale[key] = base.hex();
      return;
    }

    // Apply lightness from curve
    const newL = lightnessCurve[index];

    // Apply chroma ratio - lighter colors have lower saturation
    const newC = baseC * chromaRatios[index];

    // Keep the same hue for all levels to maintain color family
    // Only apply minimal hue rotation for darker shades to add depth
    let newH = baseH;
    if (index > baseIndex) {
      // Darker shades: slightly rotate hue for depth (max 5 degrees)
      newH = baseH + (index - baseIndex) * 1.0;
    } else {
      // Lighter shades: slightly rotate in opposite direction (max 3 degrees)
      newH = baseH - (baseIndex - index) * 0.5;
    }

    // Convert back from LCH to hex
    const newColor = chroma.lch(newL, newC, newH);
    scale[key] = newColor.hex();
  });

  return scale;
};
